import type { Bedrooms, HomeType } from '../types/funnel';

/**
 * Example property photos for home-size selection. These are illustrative reference images
 * (not company jobs, not customer homes) and are labeled “example home” in the UI.
 */
export type PropertyKey = 'apt_small' | 'apt_mid' | 'house_small' | 'house_mid' | 'house_large' | 'townhome';

export interface PropertyImage {
  url: string;
  alt: { en: string; es: string };
}

const GEN = 'https://cdn.magicpatterns.com/patterns/generated-images';

export const propertyImages: Record<PropertyKey, PropertyImage> = {
  apt_small: { url: `${GEN}/82dd57e3-d570-4c96-a101-37c5cc3f621a.jpg`, alt: { en: 'Example: two-story garden apartment building', es: 'Ejemplo: edificio de apartamentos de dos pisos' } },
  apt_mid: { url: `${GEN}/3196317b-76fd-4c25-a37b-7a9aa9938eda.jpg`, alt: { en: 'Example: mid-rise apartment community with balconies', es: 'Ejemplo: comunidad de apartamentos de varios pisos' } },
  house_small: { url: `${GEN}/9cc12771-c21c-4906-a05a-e5fea53f1502.jpg`, alt: { en: 'Example: single-story home with garage', es: 'Ejemplo: casa de un piso con garaje' } },
  house_mid: { url: `${GEN}/6490601c-9ca2-4b6c-bf4c-149b6d185b52.jpg`, alt: { en: 'Example: two-story family home with two-car garage', es: 'Ejemplo: casa de dos pisos con garaje doble' } },
  house_large: { url: `${GEN}/e80a2909-ca25-4c7d-a652-3e6d71553393.jpg`, alt: { en: 'Example: larger two-story home with three-car garage', es: 'Ejemplo: casa amplia de dos pisos con garaje triple' } },
  townhome: { url: `${GEN}/6b00ea78-668c-4664-a7c0-106aa01e38e8.jpg`, alt: { en: 'Example: row of two-story townhomes', es: 'Ejemplo: hilera de townhomes de dos pisos' } },
};

/** Picks the example property that matches the selected size and home type. */
export function propertyFor(bedrooms: Bedrooms | null, homeType: HomeType | null): PropertyKey {
  if (homeType === 'townhome') return 'townhome';
  const b = bedrooms ?? '1';
  if (homeType === 'apartment' || homeType === 'office') {
    return b === 'studio' || b === '1' || b === 'few' ? 'apt_small' : 'apt_mid';
  }
  if (homeType === 'house') {
    if (b === '5') return 'house_large';
    if (b === '4') return 'house_mid';
    return 'house_small';
  }
  // No home type yet: the usual property for that size.
  switch (b) {
    case 'studio':
    case '1':
    case 'few':
      return 'apt_small';
    case '2':
      return 'apt_mid';
    case '3':
      return 'house_small';
    case '4':
      return 'house_mid';
    default:
      return 'house_large';
  }
}
